import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Transaction } from 'ethers';
import toast from 'react-hot-toast';
import { FiX, FiCopy, FiDownload, FiUpload, FiLayers, FiZap, FiCpu, FiRefreshCw, FiExternalLink } from 'react-icons/fi';
import { useAssetClass } from '../../contexts/AssetClassContext';
import { useTokenIssuer } from '../../contexts/TokenIssuerContext';
import {
  buildSignerOutputTemplate,
  copyTextToClipboard,
  delay,
  downloadJsonFile,
  formatPreparedPayload,
  parseSignedTransactionInput,
  sanitizeContractName
} from '../../blockchain/offlineSigning';
import { usePersistedOfflineSignerAddress } from '../../hooks/usePersistedOfflineSignerAddress';
import { getOfflineSignerAddressForUser } from '../../api/offlineSignerPreferences';
import { getApiErrorMessage } from '../../api/apiErrors';
import { assetClassesApi, tokenizersApi } from '../../api/smartTokenizerApi';
import { RWA_SHARED_ERC20 } from '../../blockchain/rwaSharedErc20Artifact';
import { ContractSoliditySourcePanel } from './ContractSoliditySourcePanel';
import TransactionLookupDrawer from '../BlockchainExplorer/TransactionLookupDrawer';
import { useClientConfig } from '../../contexts/ClientConfigContext';

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.82);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 1.5rem;
`;

const Surface = styled(motion.div)`
  background: #1a1a3e;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 16px;
  width: 100%;
  max-width: min(920px, 100%);
  max-height: min(90vh, 880px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.5);
`;

const Head = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  h2 {
    margin: 0;
    color: #fff;
    font-size: 1.2rem;
    font-weight: 700;
  }
  .sub {
    margin: 0.35rem 0 0;
    color: #94a3b8;
    font-size: 0.88rem;
    line-height: 1.4;
  }
`;

const Body = styled.div`
  padding: 1.25rem 1.5rem;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
`;

const CloseBtn = styled.button`
  background: rgba(255, 255, 255, 0.08);
  border: none;
  border-radius: 8px;
  padding: 0.5rem;
  color: #cbd5e1;
  cursor: pointer;
  &:hover {
    color: #fff;
    background: rgba(255, 255, 255, 0.14);
  }
`;

const FormPanel = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-bottom: 1rem;
  color: #94a3b8;
  font-size: 0.85rem;
  span.label {
    color: #e2e8f0;
  }
  input,
  textarea {
    padding: 0.55rem 0.65rem;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.15);
    background: rgba(15, 15, 35, 0.8);
    color: #fff;
    font-size: 0.92rem;
    width: 100%;
    max-width: 100%;
  }
  textarea {
    min-height: 100px;
    font-family: Consolas, Monaco, monospace;
    font-size: 0.8rem;
    resize: vertical;
  }
`;

const FlowSteps = styled.ol`
  margin: 0 0 1rem;
  padding-left: 1.25rem;
  color: #a0a0a0;
  line-height: 1.55;
  font-size: 0.9rem;
  li {
    margin-bottom: 0.5rem;
  }
  strong {
    color: #e2e8f0;
    font-weight: 600;
  }
  code {
    font-size: 0.85em;
    color: #9bd6ff;
  }
`;

const StatusPanel = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  color: #fff;
  .title {
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: #4facfe;
  }
  .message {
    color: #a0a0a0;
    line-height: 1.5;
    margin-bottom: 1rem;
  }
  .meta {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    color: #a0a0a0;
    font-size: 0.9rem;
  }
`;

const PayloadBox = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  .title {
    color: #fff;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }
  .hint {
    color: #a0a0a0;
    font-size: 0.9rem;
    margin-bottom: 1rem;
    line-height: 1.5;
  }
  textarea {
    width: 100%;
    min-height: 160px;
    background: rgba(15, 15, 35, 0.8);
    color: #fff;
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 8px;
    padding: 1rem;
    resize: vertical;
    font-family: Consolas, Monaco, monospace;
    font-size: 0.85rem;
  }
`;

const PayloadActions = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const MiniButton = styled.button.attrs({ type: 'button' })`
  padding: 0.6rem 0.9rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.12);
  }
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const FilePickLabel = styled.label`
  padding: 0.6rem 0.9rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  cursor: pointer;
  font-size: 0.9rem;
  &:hover {
    background: rgba(255, 255, 255, 0.12);
  }
  input {
    display: none;
  }
`;

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 0.5rem;
  align-items: center;
`;

const PrimaryButton = styled.button.attrs({ type: 'button' })`
  padding: 0.75rem 1.25rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  background: #4facfe;
  color: #fff;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: #3d8bfe;
  }
  &:disabled {
    background: #2a2a3e;
    color: #a0a0a0;
    cursor: not-allowed;
  }
`;

const HashButton = styled.button.attrs({ type: 'button' })`
  background: transparent;
  border: none;
  padding: 0;
  color: #4facfe;
  font-family: Consolas, Monaco, monospace;
  cursor: pointer;
  text-decoration: underline;
`;

const SecondaryButton = styled.button.attrs({ type: 'button' })`
  padding: 0.75rem 1.25rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.14);
  }
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const Warn = styled.p`
  margin: 0 0 1rem;
  padding: 0.65rem 0.75rem;
  border-radius: 8px;
  background: rgba(251, 191, 36, 0.1);
  border: 1px solid rgba(251, 191, 36, 0.3);
  color: #fcd34d;
  font-size: 0.85rem;
  line-height: 1.45;
`;

/**
 * Offline prepare + broadcast for {@link RwaSharedErc20}.
 * Persists the fraction ERC-20 address for the given authorized tokenizer on this asset class (does not change the NFT collection address).
 */
const SectionDivider = styled.div`
  margin: 1.25rem 0;
  height: 1px;
  background: rgba(255, 255, 255, 0.08);
`;

const ManualSectionTitle = styled.h3`
  margin: 0 0 0.5rem;
  color: #e2e8f0;
  font-size: 0.95rem;
  font-weight: 600;
`;

const ManualLead = styled.p`
  margin: 0 0 1rem;
  color: #94a3b8;
  font-size: 0.85rem;
  line-height: 1.45;
`;

const OverviewTableWrap = styled.div`
  overflow-x: auto;
  margin-bottom: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
`;

const OverviewTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.78rem;

  th,
  td {
    padding: 0.5rem 0.65rem;
    text-align: left;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    vertical-align: top;
  }

  th {
    color: #94a3b8;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    font-size: 0.68rem;
  }

  td {
    color: #e2e8f0;
    vertical-align: middle;
  }

  td.mono {
    font-family: ui-monospace, monospace;
    word-break: break-all;
    font-size: 0.74rem;
  }

  tr:last-child td {
    border-bottom: none;
  }
`;

function shortenAddrTable(addr) {
  const a = String(addr || '').trim();
  if (a.length < 22) return a || '—';
  return `${a.slice(0, 12)}…${a.slice(-10)}`;
}

/** Backend uses enum AuthorizedTokenizerStatus: Approved = 2 (often JSON number). */
function isApprovedTokenizerApiStatus(raw) {
  if (raw === 2 || raw === '2') return true;
  const s = String(raw ?? '').trim().toLowerCase();
  return s === 'approved' || s === 'active';
}

function AssetClassFractionTokenDeployModal({
  assetClass,
  authorizedTokenizerId = null,
  adminFractionOverview = false,
  onClose,
  onCompleted
}) {
  const {
    prepareFractionTokenDeployment,
    submitFractionTokenDeployment,
    getAssetClassById,
    setAssetClassFractionToken
  } = useAssetClass();
  const { currentIssuer, session, isAdmin, isAuthorizedTokenizer } = useTokenIssuer();
  const { addressExplorerUrl, explorerConfigured } = useClientConfig();
  const currentUserId = currentIssuer?.userId || session?.userId || currentIssuer?.id || null;
  const [signerAddress, setSignerAddress] = usePersistedOfflineSignerAddress(currentUserId);

  const [contractName, setContractName] = useState('');
  const [preparedDeployment, setPreparedDeployment] = useState(null);
  const [signedDeploymentInput, setSignedDeploymentInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [submittedTx, setSubmittedTx] = useState(null);
  const [inspectedTxHash, setInspectedTxHash] = useState(null);
  const [manualAddrDraft, setManualAddrDraft] = useState('');
  const [manualFnDraft, setManualFnDraft] = useState('mint');
  const [tokenNameDraft, setTokenNameDraft] = useState('');
  const [tokenSymbolDraft, setTokenSymbolDraft] = useState('');
  const [manualSaving, setManualSaving] = useState(false);
  const [selectedTokenizerId, setSelectedTokenizerId] = useState('');
  const [mergedRows, setMergedRows] = useState([]);
  const [overviewLoading, setOverviewLoading] = useState(false);

  const mergedRowsWithToken = useMemo(
    () => mergedRows.filter((r) => Boolean(String(r.fractionTokenAddress || '').trim())),
    [mergedRows]
  );

  const signerOutputTemplate = JSON.stringify(
    buildSignerOutputTemplate({ summary: 'fraction ERC-20 (shares) contract (fractionalization) deployment' }),
    null,
    2
  );

  useEffect(() => {
    if (!assetClass) return;
    setContractName(sanitizeContractName(`${assetClass.name}FractionShares`));
    const sym = String(assetClass.marketSymbol || 'FRAC')
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .slice(0, 11);
    setTokenNameDraft(`${String(assetClass.name || 'Asset').trim()} fractional shares`);
    setTokenSymbolDraft(sym || 'FRAC');
    setPreparedDeployment(null);
    setSignedDeploymentInput('');
    setSubmittedTx(null);
    setInspectedTxHash(null);
    if (!adminFractionOverview) {
      setManualAddrDraft(String(assetClass.fractionTokenContractAddress || '').trim());
      setManualFnDraft(String(assetClass.fractionTokenMintFunctionName || 'mint').trim() || 'mint');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- reset when switching class id only (same pattern as deploy-contract modal)
  }, [
    assetClass?.id,
    authorizedTokenizerId,
    adminFractionOverview,
    assetClass?.fractionTokenContractAddress,
    assetClass?.fractionTokenMintFunctionName,
    assetClass?.name,
    assetClass?.marketSymbol
  ]);

  useEffect(() => {
    if (authorizedTokenizerId) {
      setSelectedTokenizerId(String(authorizedTokenizerId).trim());
    } else if (adminFractionOverview) {
      setSelectedTokenizerId('');
    }
  }, [assetClass?.id, authorizedTokenizerId, adminFractionOverview]);

  const loadAdminOverview = useCallback(async () => {
    if (!adminFractionOverview || !assetClass?.id) {
      return [];
    }
    setOverviewLoading(true);
    try {
      const [{ data: fractionRows }, { data: tokList }] = await Promise.all([
        assetClassesApi.listTokenizerFractionTokens(assetClass.id),
        tokenizersApi.list()
      ]);
      const fractionList = Array.isArray(fractionRows) ? fractionRows : [];
      const fractionMap = new Map(
        fractionList.map((v) => {
          const id = String(v.authorizedTokenizerId ?? v.AuthorizedTokenizerId ?? '');
          return [
            id,
            {
              fractionTokenAddress: String(
                v.fractionTokenContractAddress ?? v.FractionTokenContractAddress ?? ''
              ).trim(),
              mintFunction: String(v.fractionTokenMintFunctionName ?? v.FractionTokenMintFunctionName ?? '').trim()
            }
          ];
        })
      );
      const rows = Array.isArray(tokList) ? tokList : [];
      const approved = rows.filter((t) => isApprovedTokenizerApiStatus(t.status ?? t.Status));
      const merged = approved.map((t) => {
        const tokenizerId = String(t.id ?? t.Id ?? '').trim();
        const reg = t.registrationNumber ?? t.RegistrationNumber ?? '';
        const name = t.organizationName ?? t.OrganizationName ?? tokenizerId;
        const label = reg ? `${reg} · ${name}` : name;
        const v = fractionMap.get(tokenizerId) || { fractionTokenAddress: '', mintFunction: '' };
        return {
          tokenizerId,
          label,
          fractionTokenAddress: v.fractionTokenAddress,
          mintFunction: v.mintFunction || 'mint'
        };
      }).filter((r) => r.tokenizerId.length > 0);
      setMergedRows(merged);
      return merged;
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not load fraction ERC-20 (shares) contract data for tokenizers'));
      setMergedRows([]);
      return [];
    } finally {
      setOverviewLoading(false);
    }
  }, [adminFractionOverview, assetClass?.id]);

  useEffect(() => {
    if (!adminFractionOverview) return;
    loadAdminOverview();
  }, [adminFractionOverview, loadAdminOverview]);

  useEffect(() => {
    if (!adminFractionOverview) return;
    if (!selectedTokenizerId) {
      setManualAddrDraft('');
      setManualFnDraft('mint');
      return;
    }
    const row = mergedRows.find((r) => r.tokenizerId === selectedTokenizerId);
    if (row) {
      setManualAddrDraft(String(row.fractionTokenAddress || '').trim());
      setManualFnDraft(String(row.mintFunction || 'mint').trim() || 'mint');
    }
  }, [adminFractionOverview, selectedTokenizerId, mergedRows]);

  useEffect(() => {
    if (!assetClass || !currentUserId) return;
    if (getOfflineSignerAddressForUser(currentUserId)) return;
    const fromProfile = String(currentIssuer?.walletAddress || '').trim();
    if (fromProfile) setSignerAddress(fromProfile);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- bootstrap signer when class id or profile changes
  }, [assetClass?.id, currentUserId, currentIssuer?.walletAddress, setSignerAddress]);

  /** Asset Class Manager (tokenizer): fill offline signer from AuthorizedTokenizer.walletAddress (API). */
  useEffect(() => {
    if (adminFractionOverview || !authorizedTokenizerId || !assetClass?.id) return;
    if (!isAuthorizedTokenizer || isAdmin) return;
    let cancelled = false;
    tokenizersApi
      .getById(authorizedTokenizerId)
      .then(({ data }) => {
        if (cancelled || !data) return;
        const w = String(data.walletAddress ?? data.WalletAddress ?? '').trim();
        if (!/^0x[a-fA-F0-9]{40}$/i.test(w)) return;
        if (/^0x0{40}$/i.test(w)) return;
        setSignerAddress(w);
      })
      .catch(() => {
        /* optional: profile fallback already handled above */
      });
    return () => {
      cancelled = true;
    };
  }, [
    assetClass?.id,
    authorizedTokenizerId,
    adminFractionOverview,
    isAdmin,
    isAuthorizedTokenizer,
    setSignerAddress
  ]);

  if (!assetClass) return null;

  const tokenizerIdStr = adminFractionOverview
    ? String(selectedTokenizerId || '').trim()
    : String(authorizedTokenizerId || '').trim();

  if (!adminFractionOverview && !tokenizerIdStr) return null;

  const fractionTokenConfigured = adminFractionOverview
    ? Boolean(String(mergedRows.find((r) => r.tokenizerId === tokenizerIdStr)?.fractionTokenAddress || '').trim())
    : Boolean(String(assetClass.fractionTokenContractAddress || '').trim());
  const nftDeployed = Boolean(String(assetClass.contractAddress || '').trim());

  const resetOfflineFlow = () => {
    setPreparedDeployment(null);
    setSignedDeploymentInput('');
  };

  const pollForFractionToken = async (assetClassId) => {
    for (let attempt = 0; attempt < 12; attempt += 1) {
      const refreshed = await getAssetClassById(assetClassId, true);
      if (refreshed?.fractionTokenContractAddress) {
        return refreshed;
      }
      await delay(5000);
    }
    return getAssetClassById(assetClassId, true);
  };

  const handleCopyPayload = async (payload) => {
    await copyTextToClipboard(formatPreparedPayload(payload));
    toast.success('Copied JSON');
  };

  const handleManualSave = async () => {
    if (!tokenizerIdStr) {
      toast.error(
        adminFractionOverview ? 'Select an authorized tokenizer to configure.' : 'Authorized tokenizer is required.'
      );
      return;
    }
    setManualSaving(true);
    try {
      await setAssetClassFractionToken(assetClass.id, tokenizerIdStr, {
        fractionTokenContractAddress: manualAddrDraft.trim() || null,
        fractionTokenMintFunctionName: manualFnDraft.trim() || null
      });
      try {
        await getAssetClassById(assetClass.id, true);
      } catch {
        /* refresh is best-effort */
      }
      onCompleted?.();
      if (adminFractionOverview) {
        await loadAdminOverview();
      }
    } catch {
      /* toast in context */
    } finally {
      setManualSaving(false);
    }
  };

  const handlePrepare = async () => {
    if (!tokenizerIdStr) {
      toast.error(
        adminFractionOverview ? 'Select an authorized tokenizer to configure.' : 'Authorized tokenizer is required.'
      );
      return;
    }
    if (!currentUserId) {
      toast.error('Please sign in before preparing deployment.');
      return;
    }
    if (!signerAddress.trim()) {
      toast.error('Offline signer address is required.');
      return;
    }
    const bc = String(RWA_SHARED_ERC20.bytecode || '').trim();
    if (!bc.startsWith('0x') || bc.length < 4) {
      toast.error('ERC-20 bytecode is missing. Re-run contracts:build in the app repo.');
      return;
    }

    const ctor = [
      (tokenNameDraft.trim() || `${assetClass.name} fractional shares`).trim(),
      (tokenSymbolDraft.trim() || 'FRAC').trim(),
      JSON.stringify([signerAddress.trim()])
    ];

    setBusy(true);
    try {
      const name = sanitizeContractName(contractName || `${assetClass.name}FractionShares`);
      const deploymentPayload = await prepareFractionTokenDeployment(assetClass.id, tokenizerIdStr, {
        assetClassId: assetClass.id,
        contractName: name,
        signerAddress: signerAddress.trim(),
        contractAbi: RWA_SHARED_ERC20.abiJson,
        bytecode: RWA_SHARED_ERC20.bytecode,
        constructorParameters: ctor
      });
      const txData = String(deploymentPayload?.data ?? '').trim();
      if (deploymentPayload == null || typeof deploymentPayload !== 'object' || !txData) {
        toast.error(
          'Prepare deployment returned an empty or invalid payload (missing transaction data). Check the network response for this request.'
        );
        return;
      }
      setPreparedDeployment(deploymentPayload);
      toast.success('Unsigned fraction ERC-20 (shares) contract deployment ready.');
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Prepare fraction ERC-20 (shares) contract deployment failed'));
    } finally {
      setBusy(false);
    }
  };

  const handleSubmitSigned = async () => {
    if (!tokenizerIdStr) {
      toast.error(
        adminFractionOverview ? 'Select an authorized tokenizer to configure.' : 'Authorized tokenizer is required.'
      );
      return;
    }
    setBusy(true);
    try {
      const parsedSigned = parseSignedTransactionInput(signedDeploymentInput);
      const signedDeployment = Array.isArray(parsedSigned) ? parsedSigned[0] : parsedSigned;
      let txHash = String(signedDeployment.transactionHash || '').trim();
      if (!txHash) {
        try {
          txHash = Transaction.from(signedDeployment.signedTransactionHex).hash || '';
        } catch {
          /* hash is optional for success UI */
        }
      }
      const ctor = [
        (tokenNameDraft.trim() || `${assetClass.name} fractional shares`).trim(),
        (tokenSymbolDraft.trim() || 'FRAC').trim(),
        JSON.stringify([signerAddress.trim()])
      ];
      const name = sanitizeContractName(contractName || `${assetClass.name}FractionShares`);
      await submitFractionTokenDeployment(assetClass.id, tokenizerIdStr, {
        assetClassId: assetClass.id,
        contractName: name,
        signedTransactionHex: signedDeployment.signedTransactionHex,
        expectedContractAddress: signedDeployment.expectedContractAddress,
        contractAbi: RWA_SHARED_ERC20.abiJson,
        bytecode: null,
        constructorParameters: ctor
      });
      resetOfflineFlow();
      let fractionTokenAddress = '';
      if (adminFractionOverview) {
        const merged = await loadAdminOverview();
        const row = merged.find((r) => r.tokenizerId === tokenizerIdStr);
        fractionTokenAddress = String(row?.fractionTokenAddress || '').trim();
      } else {
        const refreshed = await pollForFractionToken(assetClass.id);
        fractionTokenAddress = String(refreshed?.fractionTokenContractAddress || '').trim();
      }
      onCompleted?.();
      setSubmittedTx({ hash: txHash, status: 'submitted', fractionTokenAddress });
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Broadcast fraction ERC-20 (shares) contract deployment failed'));
    } finally {
      setBusy(false);
    }
  };

  const handleSignedFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || '').trim();
      if (!text) {
        toast.error('File was empty');
        return;
      }
      setSignedDeploymentInput(text);
      toast.success(`Loaded ${file.name}`);
    };
    reader.onerror = () => toast.error('Could not read file');
    reader.readAsText(file, 'utf-8');
    event.target.value = '';
  };

  return (
    <>
      <TransactionLookupDrawer transactionHash={inspectedTxHash} onClose={() => setInspectedTxHash(null)} />
      <AnimatePresence>
        <Overlay
          key={assetClass.id || 'deploy-fraction-token'}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={(e) => e.target === e.currentTarget && !busy && onClose()}
        >
          <Surface
            initial={{ scale: 0.94, opacity: 0, y: 12 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.94, opacity: 0, y: 12 }}
            onClick={(e) => e.stopPropagation()}
          >
            <Head>
              <div>
                <h2>
                  <FiLayers style={{ verticalAlign: '-3px', marginRight: '0.35rem', color: '#c4b5fd' }} />
                  fraction ERC-20 (shares) contract (fractionalization)
                </h2>
                <p className="sub">
                  {assetClass.name}
                  <span
                    style={{
                      display: 'block',
                      marginTop: '0.35rem',
                      fontSize: '0.88rem',
                      color: '#94a3b8',
                      lineHeight: 1.45
                    }}
                  >
                    Set an existing ERC-20 address manually below, or deploy <strong>RwaSharedErc20</strong> via offline signing
                    (same pattern as <strong>Deploy smart contract</strong> for the class NFT).
                  </span>
                  {adminFractionOverview ? (
                    <span style={{ display: 'block', marginTop: '0.35rem', fontSize: '0.82rem', color: '#64748b' }}>
                      Administrator view: review the <strong>approved tokenizers</strong> table, then choose a tokenizer in{' '}
                      <strong>Authorized tokenizer to configure</strong> to edit or deploy.
                    </span>
                  ) : (
                    <span style={{ display: 'block', marginTop: '0.35rem', fontSize: '0.82rem', color: '#64748b' }}>
                      Authorized tokenizer: <code style={{ color: '#cbd5e1' }}>{tokenizerIdStr}</code>
                    </span>
                  )}
                </p>
              </div>
              <CloseBtn type="button" onClick={onClose} aria-label="Close" disabled={busy}>
                <FiX size={22} />
              </CloseBtn>
            </Head>

            <Body>
              {!nftDeployed ? (
                <Warn>
                  Deploy the class NFT collection contract first. The fraction ERC-20 is a separate contract used to mint share tokens after
                  the underlying NFT is locked in the class custody vault.
                </Warn>
              ) : null}

              {submittedTx ? (
                <StatusPanel>
                  <div className="title">Deployment submitted</div>
                  <div className="message">
                    The Core Tokenizer backend broadcast the signed deployment via <code>eth_sendRawTransaction</code> to the
                    Ping chain. Open the hash below to inspect the receipt, or copy the token address and close when
                    done.
                  </div>
                  <div className="meta">
                    {submittedTx.hash ? (
                      <span>
                        Transaction:{' '}
                        <HashButton type="button" onClick={() => setInspectedTxHash(submittedTx.hash)}>
                          {submittedTx.hash}
                        </HashButton>
                      </span>
                    ) : (
                      <span>Transaction hash could not be derived from the signed payload.</span>
                    )}
                    {submittedTx.fractionTokenAddress ? (
                      <span style={{ wordBreak: 'break-all' }}>Fraction token: {submittedTx.fractionTokenAddress}</span>
                    ) : null}
                  </div>
                  <ActionRow style={{ marginTop: '1rem' }}>
                    {submittedTx.fractionTokenAddress ? (
                      <MiniButton
                        type="button"
                        onClick={() => {
                          copyTextToClipboard(submittedTx.fractionTokenAddress);
                          toast.success('Copied');
                        }}
                      >
                        <FiCopy /> Copy token address
                      </MiniButton>
                    ) : null}
                    <PrimaryButton type="button" onClick={() => onClose()}>
                      Close
                    </PrimaryButton>
                  </ActionRow>
                </StatusPanel>
              ) : null}

              {!submittedTx && nftDeployed ? (
                <>
                  {adminFractionOverview ? (
                    <FormPanel>
                      <ManualSectionTitle>Approved authorized tokenizers (this class)</ManualSectionTitle>
                      <ManualLead>
                        Tokenizers listed here already have a <strong>fraction ERC-20 address</strong> on file for this class. Use the
                        explorer icon in each row to open the token contract in the configured block explorer. To configure
                        or deploy for a tokenizer without a token yet, use the dropdown below.
                      </ManualLead>
                      <ActionRow style={{ marginBottom: '0.75rem' }}>
                        <SecondaryButton
                          type="button"
                          onClick={() => loadAdminOverview()}
                          disabled={overviewLoading}
                        >
                          <FiRefreshCw />
                          {overviewLoading ? 'Refreshing…' : 'Refresh'}
                        </SecondaryButton>
                      </ActionRow>
                      {overviewLoading && mergedRows.length === 0 ? (
                        <p style={{ color: '#94a3b8', margin: '0 0 1rem' }}>Loading…</p>
                      ) : (
                        <OverviewTableWrap>
                          <OverviewTable>
                            <thead>
                              <tr>
                                <th>Tokenizer</th>
                                <th>Tokenizer ID</th>
                                <th>Token address</th>
                                <th>Mint function</th>
                                <th style={{ whiteSpace: 'nowrap' }}>Explorer</th>
                              </tr>
                            </thead>
                            <tbody>
                              {mergedRows.length === 0 ? (
                                <tr>
                                  <td colSpan={5} style={{ color: '#94a3b8' }}>
                                    No approved authorized tokenizers found.
                                  </td>
                                </tr>
                              ) : mergedRowsWithToken.length === 0 ? (
                                <tr>
                                  <td colSpan={5} style={{ color: '#94a3b8' }}>
                                    No fraction ERC-20 (shares) contract address is saved yet for any approved tokenizer on this class. Use{' '}
                                    <strong>Authorized tokenizer to configure</strong> below to set an address or deploy.
                                  </td>
                                </tr>
                              ) : (
                                mergedRowsWithToken.map((r) => {
                                  const addr = String(r.fractionTokenAddress || '').trim();
                                  const url = explorerConfigured ? addressExplorerUrl(addr) : '';
                                  return (
                                    <tr key={r.tokenizerId}>
                                      <td>{r.label}</td>
                                      <td className="mono">{r.tokenizerId}</td>
                                      <td className="mono">{shortenAddrTable(addr)}</td>
                                      <td className="mono">{r.mintFunction || 'mint'}</td>
                                      <td>
                                        <MiniButton
                                          type="button"
                                          aria-label="View fraction token contract in block explorer"
                                          disabled={!explorerConfigured || !url}
                                          title={
                                            explorerConfigured
                                              ? 'Open fraction token contract in block explorer'
                                              : 'Block explorer URL not loaded from API'
                                          }
                                          onClick={() => {
                                            if (url) window.open(url, '_blank', 'noopener,noreferrer');
                                          }}
                                        >
                                          <FiExternalLink aria-hidden />
                                        </MiniButton>
                                      </td>
                                    </tr>
                                  );
                                })
                              )}
                            </tbody>
                          </OverviewTable>
                        </OverviewTableWrap>
                      )}
                    </FormPanel>
                  ) : null}

                  {adminFractionOverview ? (
                    <FormPanel>
                      <ManualSectionTitle>Authorized tokenizer to configure</ManualSectionTitle>
                      <ManualLead>
                        Choose which approved tokenizer&apos;s fraction ERC-20 (shares) contract you are editing or deploying. The overview
                        table above lists only tokenizers that already have a fraction token address; this list includes every
                        approved tokenizer.
                      </ManualLead>
                      <Field>
                        <span className="label">Tokenizer</span>
                        <select
                          value={selectedTokenizerId}
                          onChange={(e) => setSelectedTokenizerId(e.target.value)}
                          style={{
                            padding: '0.55rem 0.65rem',
                            borderRadius: 8,
                            border: '1px solid rgba(255, 255, 255, 0.15)',
                            background: 'rgba(15, 15, 35, 0.8)',
                            color: '#fff',
                            width: '100%',
                            maxWidth: '100%',
                            fontSize: '0.92rem'
                          }}
                        >
                          <option value="">Select tokenizer…</option>
                          {mergedRows.map((r) => (
                            <option key={r.tokenizerId} value={r.tokenizerId}>
                              {r.label}
                            </option>
                          ))}
                        </select>
                      </Field>
                      {!overviewLoading && mergedRows.length === 0 ? (
                        <p style={{ color: '#fcd34d', fontSize: '0.82rem', margin: '0.5rem 0 0', lineHeight: 1.45 }}>
                          No approved tokenizers returned from the API. Ensure at least one authorized tokenizer is
                          approved, or use Refresh after creating one.
                        </p>
                      ) : null}
                    </FormPanel>
                  ) : null}

                  {adminFractionOverview ? <SectionDivider /> : null}

                  <FormPanel>
                    <ManualSectionTitle>Set an existing ERC-20 address and mint function manually</ManualSectionTitle>
                    <ManualLead>
                      Link a fraction token contract that is already deployed, or clear the address and save to remove the record so
                      you can deploy a new token using the offline wizard below.
                    </ManualLead>
                    <Field>
                      <span className="label">Fraction token contract address</span>
                      <input
                        value={manualAddrDraft}
                        onChange={(e) => setManualAddrDraft(e.target.value)}
                        placeholder="0x… (empty clears)"
                        autoComplete="off"
                        disabled={manualSaving || busy || (adminFractionOverview && !tokenizerIdStr)}
                      />
                    </Field>
                    <Field>
                      <span className="label">Mint function on ERC-20</span>
                      <input
                        value={manualFnDraft}
                        onChange={(e) => setManualFnDraft(e.target.value)}
                        placeholder="mint"
                        autoComplete="off"
                        disabled={manualSaving || busy || (adminFractionOverview && !tokenizerIdStr)}
                      />
                    </Field>
                    <ActionRow style={{ marginTop: 0 }}>
                      <SecondaryButton
                        type="button"
                        onClick={handleManualSave}
                        disabled={manualSaving || busy || (adminFractionOverview && !tokenizerIdStr)}
                      >
                        {manualSaving ? 'Saving…' : 'Save fraction ERC-20 (shares) contract'}
                      </SecondaryButton>
                    </ActionRow>
                  </FormPanel>

                  <SectionDivider />

                  {!fractionTokenConfigured ? (
                    <>
                      <ManualSectionTitle style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                        <FiZap style={{ color: '#4facfe' }} aria-hidden />
                        Deploy fraction token contract
                      </ManualSectionTitle>
                      <ManualLead style={{ marginBottom: '1rem' }}>
                        Core contract deployment wizard — offline signing &amp; broadcast on Ping.
                      </ManualLead>

                      <Warn>
                        This flow deploys the in-repo <strong>RwaSharedErc20</strong> contract. It does{' '}
                        <strong>not</strong> change the class NFT contract address — only the fraction ERC-20 (shares) contract record for this
                        tokenizer on the asset class.
                      </Warn>

                      <FormPanel>
                        <ContractSoliditySourcePanel
                          title="Contract source (Solidity)"
                          fileName={RWA_SHARED_ERC20.sourceFileName}
                          source={RWA_SHARED_ERC20.soliditySource}
                          variant="prebuilt"
                          note="Embedded ABI and bytecode are generated from this file (npm run contracts:build)."
                        />
                        <Field>
                          <span className="label">Contract name (on-chain / records)</span>
                          <input value={contractName} onChange={(e) => setContractName(e.target.value)} />
                        </Field>
                        <Field>
                          <span className="label">ERC-20 name (constructor)</span>
                          <input
                            value={tokenNameDraft}
                            onChange={(e) => setTokenNameDraft(e.target.value)}
                            placeholder="e.g. My asset fractional shares"
                            autoComplete="off"
                          />
                        </Field>
                        <Field>
                          <span className="label">ERC-20 symbol (constructor)</span>
                          <input
                            value={tokenSymbolDraft}
                            onChange={(e) => setTokenSymbolDraft(e.target.value)}
                            placeholder="e.g. FRAC"
                            autoComplete="off"
                          />
                        </Field>
                        <Field>
                          <span className="label">Offline signer address</span>
                          <input
                            value={signerAddress}
                            onChange={(e) => setSignerAddress(e.target.value)}
                            placeholder="0x…"
                            autoComplete="off"
                          />
                          <span style={{ color: '#64748b', fontSize: '0.78rem' }}>
                            Must match the address used by your offline signer. Remembered for your account in this browser.
                          </span>
                        </Field>
                      </FormPanel>

                      <StatusPanel>
                        <div className="title">Offline signing &amp; broadcast (Ping chain)</div>
                        <FlowSteps>
                          <li>
                            <strong>Prepare unsigned deployment</strong> — The API builds the unsigned contract-creation
                            transaction JSON your offline signer needs. Private keys never leave the signer. You must be
                            allowed to manage this tokenizer&apos;s fraction token (admin or that tokenizer&apos;s operator).
                          </li>
                          <li>
                            <strong>Sign</strong> — Copy or download the JSON, then sign with the offline signer address you
                            entered (cold wallet or Ping offline signer app).
                          </li>
                          <li>
                            <strong>Upload or paste signed data</strong> — Load the signer output file or paste JSON / raw{' '}
                            <code>0x</code> <code>signedTransactionHex</code>. Then{' '}
                            <strong>Broadcast signed deployment</strong> so the backend submits{' '}
                            <code>eth_sendRawTransaction</code> and saves the token address. Default mint function remains{' '}
                            <code>mint</code> on the ERC-20.
                          </li>
                        </FlowSteps>
                        <div className="meta">
                          <span>Asset class: {assetClass.code || assetClass.name}</span>
                          <span>Contract: RwaSharedErc20</span>
                        </div>
                      </StatusPanel>

                      {preparedDeployment && (
                        <>
                          <PayloadBox>
                            <div className="title">Unsigned contract deployment transaction</div>
                            <div className="hint">Sign offline only — do not sign in the browser.</div>
                            <PayloadActions>
                              <MiniButton type="button" onClick={() => handleCopyPayload(preparedDeployment)}>
                                <FiCopy />
                                Copy JSON
                              </MiniButton>
                              <MiniButton
                                type="button"
                                onClick={() =>
                                  downloadJsonFile(
                                    `${assetClass.code || 'asset-class'}-fraction-token-deploy-prepared.json`,
                                    preparedDeployment
                                  )
                                }
                              >
                                <FiDownload />
                                Download JSON
                              </MiniButton>
                            </PayloadActions>
                            <textarea readOnly value={formatPreparedPayload(preparedDeployment)} />
                          </PayloadBox>

                          <PayloadBox>
                            <div className="title">Signer output template</div>
                            <div className="hint">Your signer should produce JSON containing signedTransactionHex.</div>
                            <PayloadActions>
                              <MiniButton
                                type="button"
                                onClick={async () => {
                                  await copyTextToClipboard(signerOutputTemplate);
                                  toast.success('Copied template');
                                }}
                              >
                                <FiCopy />
                                Copy template
                              </MiniButton>
                            </PayloadActions>
                            <textarea readOnly value={signerOutputTemplate} />
                          </PayloadBox>

                          <PayloadBox>
                            <div className="title">Signed deployment — upload or paste</div>
                            <div className="hint">
                              Upload a JSON file from your signer or paste JSON / raw <code>signedTransactionHex</code>.
                            </div>
                            <PayloadActions>
                              <FilePickLabel>
                                <FiUpload aria-hidden />
                                Upload signed file
                                <input
                                  type="file"
                                  accept=".json,.txt,text/json,application/json"
                                  onChange={handleSignedFile}
                                />
                              </FilePickLabel>
                            </PayloadActions>
                            <textarea
                              value={signedDeploymentInput}
                              onChange={(e) => setSignedDeploymentInput(e.target.value)}
                              placeholder="Paste signedTransactionHex or full signer JSON here…"
                            />
                          </PayloadBox>
                        </>
                      )}

                      <ActionRow>
                        <SecondaryButton
                          type="button"
                          onClick={resetOfflineFlow}
                          disabled={busy || (!preparedDeployment && !signedDeploymentInput)}
                        >
                          <FiRefreshCw />
                          Clear prepared / signed
                        </SecondaryButton>
                        <PrimaryButton
                          type="button"
                          onClick={handlePrepare}
                          disabled={
                            busy ||
                            Boolean(preparedDeployment) ||
                            !nftDeployed ||
                            (adminFractionOverview && !tokenizerIdStr)
                          }
                        >
                          <FiCpu />
                          Prepare unsigned deployment
                        </PrimaryButton>
                        <PrimaryButton
                          type="button"
                          onClick={handleSubmitSigned}
                          disabled={
                            busy ||
                            !preparedDeployment ||
                            !signedDeploymentInput.trim() ||
                            (adminFractionOverview && !tokenizerIdStr)
                          }
                        >
                          <FiCpu />
                          Broadcast signed deployment
                        </PrimaryButton>
                      </ActionRow>
                    </>
                  ) : (
                    <p style={{ color: '#94a3b8', margin: 0, lineHeight: 1.55, fontSize: '0.88rem' }}>
                      A fraction token is already configured for this tokenizer. To deploy a <strong>new</strong> token with offline
                      signing, clear the token address in the section above, save, then reopen this dialog — the deployment
                      wizard will appear when no token is on file.
                    </p>
                  )}
                </>
              ) : null}
            </Body>
          </Surface>
        </Overlay>
      </AnimatePresence>
    </>
  );
}

export default AssetClassFractionTokenDeployModal;

